//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import { type AttachedDoc, type Class, type Doc, type Ref, type Timestamp } from '@hcengineering/core'
import { type Card } from '@hcengineering/card'
import { type Employee } from '@hcengineering/contact'
import { type Asset, type IntlString, plugin, type Plugin } from '@hcengineering/platform'

/** @public */
export type WidgetKind =
  | 'count'
  | 'bar'
  | 'donut'
  | 'burndown'
  | 'recentActivity'
  | 'topAssignees'
  | 'goalProgress'
  | 'customQuery'
  | 'taskList'
  | 'calculation'
  | 'workload'
  | 'velocity'
  | 'timeTracking'

/** @public */
export interface DashboardPeriod {
  kind: 'last_30d' | 'this_week' | 'custom'
  from?: Timestamp
  to?: Timestamp
}

/** @public */
export interface WidgetPosition {
  x: number
  y: number
}

/** @public */
export interface WidgetSize {
  w: number
  h: number
}

/**
 * Dashboard — user-composable widget grid (Card subtype, per spec §6.7).
 * @public
 */
export interface Dashboard extends Card {
  owner: Ref<Employee>
  period: DashboardPeriod
  widgets?: number
  publicLink?: string
}

/**
 * Widget — child of Dashboard. Free-form `config` per widget kind.
 * @public
 */
export interface Widget extends AttachedDoc<Dashboard> {
  kind: WidgetKind
  position: WidgetPosition
  size: WidgetSize
  config: Record<string, unknown>
}

/** @public */
export const dashboardId = 'dashboard' as Plugin

export default plugin(dashboardId, {
  app: {
    Dashboards: '' as Ref<Doc>
  },
  class: {
    Dashboard: '' as Ref<Class<Dashboard>>,
    Widget: '' as Ref<Class<Widget>>
  },
  icon: {
    DashboardApplication: '' as Asset,
    Dashboard: '' as Asset,
    Widget: '' as Asset
  },
  string: {
    ConfigLabel: '' as IntlString,
    ConfigDescription: '' as IntlString,
    Dashboard: '' as IntlString,
    Dashboards: '' as IntlString,
    Widget: '' as IntlString,
    Widgets: '' as IntlString,
    Owner: '' as IntlString,
    Period: '' as IntlString,
    Kind: '' as IntlString,
    Position: '' as IntlString,
    Size: '' as IntlString,
    Config: '' as IntlString,
    DashboardApplication: '' as IntlString,
    NewDashboard: '' as IntlString,
    AddWidget: '' as IntlString,
    EditLayout: '' as IntlString,
    Refresh: '' as IntlString,
    DropWidgetHere: '' as IntlString,
    MeMode: '' as IntlString,
    AllAssignees: '' as IntlString,
    Configure: '' as IntlString,
    Duplicate: '' as IntlString,
    Rename: '' as IntlString,
    Delete: '' as IntlString,
    DeleteWidget: '' as IntlString,
    Templates: '' as IntlString,
    ShareLink: '' as IntlString
  }
})
